import React, { createContext, useContext, useState, useMemo, useCallback, useRef, ReactNode } from 'react';
import { intelligentChatService } from '../services/intelligentChatService';
import { langchainIntegrationService } from '../services/langchainIntegrationService';
import { DocumentReference } from '../components/ChatInDoc/DocumentReferences';
import { api } from '../services/api';
import { workspaceService } from '../services/workspaceService';
import { chatTenantService } from '../services/chatTenantService';
import { extractAccessErrorMessage } from '../utils/featureAccessError';

const CHATINDOC_WORKSPACE_STORAGE_KEY = '@KnowInDoc:chatindoc_active_workspace_id';

function resolveWorkspaceLoadError(error: unknown): { message: string; isFeatureError: boolean } {
  const featureMessage = extractAccessErrorMessage(error);
  if (featureMessage)
  {
    return { message: featureMessage, isFeatureError: true };
  }

  return {
    message: 'Não foi possível carregar as configurações do assistente. Tente novamente.',
    isFeatureError: false,
  };
}
// conversationOrchestrator removed - using LangChain orchestrator
// testBackendIntegration removed

export type ChatMessageType = 'text' | 'document' | 'system';

export interface DocumentMessageMetadata {
  userPrivateDocumentId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  processingStatus: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  rating: 'like' | 'dislike' | null;
  workspaceChatId?: string; // ID da mensagem no workspace chat (GUID)
  messageType?: ChatMessageType;
  documentMetadata?: DocumentMessageMetadata;
  documentReferences?: DocumentReference[];
  intent?: {
    primaryIntent: string;
    confidence: number;
    reasoning: string;
  };
  searchResults?: Array<{
    documentId: string;
    content: string;
    score: number;
    metadata?: Record<string, any>;
  }>;
  source?: 'langchain' | 'backend'; // Fonte da resposta (LangChain ou backend)
  imageAttachments?: ChatImageAttachment[];
}

export interface ChatImageAttachment {
  fileName: string;
  fileType: string;
  filePath: string;
  previewUrl?: string;
}

function parseDocumentMetadataFromApi(metadata: unknown): DocumentMessageMetadata | undefined {
  if (metadata == null) {
    return undefined;
  }
  let raw: unknown = metadata;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const id = o.userPrivateDocumentId;
  if (typeof id !== 'string') {
    return undefined;
  }
  return {
    userPrivateDocumentId: id,
    fileName: String(o.fileName ?? ''),
    fileType: String(o.fileType ?? ''),
    fileSizeBytes: Number(o.fileSizeBytes ?? 0),
    processingStatus: String(o.processingStatus ?? 'pending')
  };
}

export function mapWorkspaceHistoryMessage(msg: any): ChatMessage {
  const roleRaw = (msg.role as string) || 'user';
  const role: 'user' | 'assistant' = roleRaw === 'user' ? 'user' : 'assistant';
  const messageTypeRaw = (msg.messageType ?? msg.MessageType) as string | undefined;
  const messageType: ChatMessageType | undefined =
    messageTypeRaw === 'document' || messageTypeRaw === 'system' || messageTypeRaw === 'text'
      ? messageTypeRaw
      : undefined;
  const metadataSource = msg.metadata ?? msg.Metadata;
  const documentMetadata =
    messageType === 'document' ? parseDocumentMetadataFromApi(metadataSource) : undefined;
  const imageAttachments = parseChatImageAttachments(msg.attachments ?? msg.Attachments);

  return {
    id: msg.id || Math.random().toString(36).substring(2, 10),
    role,
    content: msg.content || msg.message || '',
    timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
    rating:
      msg.evaluation === true ? 'like' : msg.evaluation === false ? 'dislike' : null,
    workspaceChatId: msg.id,
    messageType: messageType ?? 'text',
    documentMetadata,
    documentReferences: msg.documentReferences || [],
    intent: msg.intent,
    searchResults: msg.searchResults,
    imageAttachments
  };
}

function parseChatImageAttachments(raw: unknown): ChatImageAttachment[] | undefined {
  let value = raw;
  if (typeof value === 'string')
  {
    try
    {
      value = JSON.parse(value);
    }
    catch
    {
      return undefined;
    }
  }

  if (!Array.isArray(value))
  {
    return undefined;
  }

  const items: ChatImageAttachment[] = [];
  for (const entry of value)
  {
    if (!entry || typeof entry !== 'object')
    {
      continue;
    }

    const item = entry as Record<string, unknown>;
    const filePath = item.filePath ?? item.FilePath;
    const fileType = item.fileType ?? item.FileType;
    if (typeof filePath !== 'string' || typeof fileType !== 'string')
    {
      continue;
    }

    if (!fileType.toLowerCase().startsWith('image/'))
    {
      continue;
    }

    const fileName = item.fileName ?? item.FileName;
    items.push({
      fileName: typeof fileName === 'string' ? fileName : 'Imagem',
      fileType,
      filePath,
    });
  }

  if (items.length === 0)
  {
    return undefined;
  }

  return items;
}

export interface ChatThread {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  backendThreadId?: string; // ID da thread no backend
}

export interface ChatInDocWorkspace {
  id: string;
  name: string;
  slug: string;
  assistantName: string;
  description: string;
  avatar?: string;
  isActive?: boolean;
}

interface ChatInDocContextType {
  threads: ChatThread[];
  activeThreadId: string | null;
  setActiveThread: (id: string) => Promise<void>;
  /** `createInWorkspaceId`: ao criar thread nova, usa este workspace (evita corrida com `activeWorkspaceId` após troca de agente). */
  addThread: (title?: string, existingThreadId?: string, createInWorkspaceId?: string) => Promise<string>;
  addMessage: (threadId: string, message: ChatMessage) => Promise<void>;
  updateMessage: (threadId: string, messageId: string, updates: Partial<ChatMessage>) => void;
  removeThread: (id: string) => Promise<void>;
  // Controle da janela flutuante
  floatingThreadId: string | null;
  showFloatingChat: (threadId: string) => void;
  hideFloatingChat: () => void;
  // Status de conexão
  isBackendConnected: boolean;
  connectionStatus: string;
  /** Agentes/workspaces disponíveis para o chat (tenant). */
  chatWorkspaces: ChatInDocWorkspace[];
  // Workspace do ChatInDoc (agente ativo)
  workspace: ChatInDocWorkspace | null;
  /** Id do workspace/agente em uso; alinhar threads e stream ao mesmo agente. */
  activeWorkspaceId: string | null;
  /** Troca o agente ativo; pode limpar conversas locais após confirmação. */
  selectWorkspace: (workspaceId: string) => Promise<void>;
  /** Troca o agente sem diálogo (ex.: fluxo @mention → nova thread em outro workspace). */
  forceSelectWorkspace: (workspaceId: string) => Promise<void>;
  /** Recarrega a lista de agentes (ex.: após criar/excluir no admin ou trocar tenant). */
  refreshChatWorkspaces: (options?: { forceNewSelection?: boolean }) => Promise<void>;
  /** Limpa conversas locais ao trocar o tenant ativo do chat. */
  resetForTenantChange: () => void;
  isLoadingWorkspace: boolean;
  workspaceLoadError: string | null;
  isWorkspaceFeatureError: boolean;
  // Funcionalidades de inteligência
  analyzeIntent: (query: string, threadId?: string) => Promise<any>;
  searchIntelligently: (query: string, threadId?: string) => Promise<any>;
  detectAmbiguity: (query: string, threadId?: string) => Promise<any>;
  // NOVAS FUNCIONALIDADES PARA TESTE
  startIntelligentConversation: (query: string, threadId?: string) => Promise<any>;
  testBackendIntegrationDirectly: (query: string, filters?: any) => Promise<string>;
  runCompleteTest: () => Promise<string>;
}

const ChatInDocContext = createContext<ChatInDocContextType | undefined>(undefined);

export const ChatInDocProvider = ({ children }: { children: ReactNode }) => {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [floatingThreadId, setFloatingThreadId] = useState<string | null>(null);
  const [isBackendConnected, setIsBackendConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Verificando conexão...');
  const [chatWorkspaces, setChatWorkspaces] = useState<ChatInDocWorkspace[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [threadsHydratedForWorkspaceId, setThreadsHydratedForWorkspaceId] = useState<string | null>(null);
  const [isLoadingWorkspace, setIsLoadingWorkspace] = useState(true);
  const [workspaceLoadError, setWorkspaceLoadError] = useState<string | null>(null);
  const [isWorkspaceFeatureError, setIsWorkspaceFeatureError] = useState(false);
  const workspaceRefreshGenerationRef = useRef(0);

  const workspace = useMemo(
    () => chatWorkspaces.find(w => w.id === activeWorkspaceId) ?? null,
    [chatWorkspaces, activeWorkspaceId]
  );

  const applyChatWorkspaceList = useCallback((
    workspaceList: ChatInDocWorkspace[],
    forceNewSelection = false
  ) => {
    setChatWorkspaces(workspaceList);

    const stored = sessionStorage.getItem(CHATINDOC_WORKSPACE_STORAGE_KEY);
    const validStored = !forceNewSelection && stored && workspaceList.some(w => w.id === stored);
    const fallback =
      workspaceList.find(w => w.isActive !== false) ?? workspaceList[0] ?? null;

    setActiveWorkspaceId(prev => {
      if (workspaceList.length === 0) {
        return null;
      }
      if (!forceNewSelection && prev && workspaceList.some(w => w.id === prev)) {
        return prev;
      }
      const initialId = validStored ? stored! : fallback?.id ?? null;
      if (initialId) {
        sessionStorage.setItem(CHATINDOC_WORKSPACE_STORAGE_KEY, initialId);
        return initialId;
      }
      return null;
    });
  }, []);

  const fetchChatInDocWorkspace = useCallback(async (cancelled: () => boolean) => {
    try {
      setWorkspaceLoadError(null);
      setIsWorkspaceFeatureError(false);
      const summaries = await workspaceService.getChatInDocWorkspaces(undefined, {
        skipFeatureUnavailableToast: true,
      });
      if (cancelled()) {
        return;
      }

      const workspaceList: ChatInDocWorkspace[] = summaries.map(s => ({
        id: s.id,
        name: s.name,
        slug: s.slug,
        assistantName: s.assistantName,
        description: s.description,
        avatar: s.avatar,
        isActive: s.isActive,
      }));

      applyChatWorkspaceList(workspaceList);
      queueMicrotask(() => {
        if (!cancelled()) {
          setIsLoadingWorkspace(false);
        }
      });
    } catch (error) {
      if (!cancelled()) {
        console.error('Erro ao carregar workspace ChatInDoc:', error);
        const loadError = resolveWorkspaceLoadError(error);
        setWorkspaceLoadError(loadError.message);
        setIsWorkspaceFeatureError(loadError.isFeatureError);
        setChatWorkspaces([]);
        setActiveWorkspaceId(null);
        setIsLoadingWorkspace(false);
      }
    }
  }, [applyChatWorkspaceList]);

  // Verificar conexão com o backend e buscar workspace
  React.useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    const initializeChatInDoc = async () => {
      setIsLoadingWorkspace(true);
      let workspaceFetchStarted = false;
      try {
        const healthStatus = await intelligentChatService.checkBackendHealth();
        if (cancelled) {
          return;
        }

        setIsBackendConnected(healthStatus.isHealthy);
        setConnectionStatus(healthStatus.message || 'Status desconhecido');

        if (healthStatus.isHealthy) {
          workspaceFetchStarted = true;
          await fetchChatInDocWorkspace(isCancelled);
        }
      } catch (error) {
        if (!cancelled) {
          setIsBackendConnected(false);
          setConnectionStatus('Erro ao verificar conexão');
        }
      } finally {
        if (!cancelled && !workspaceFetchStarted) {
          setIsLoadingWorkspace(false);
        }
      }
    };

    initializeChatInDoc();

    return () => {
      cancelled = true;
    };
  }, [fetchChatInDocWorkspace]);

  React.useEffect(() => {
    return () => {
      chatTenantService.setActiveTenantId(null);
    };
  }, []);

  React.useEffect(() => {
    if (!activeWorkspaceId || !isBackendConnected) {
      return;
    }
    if (!chatWorkspaces.some(w => w.id === activeWorkspaceId)) {
      return;
    }
    if (threadsHydratedForWorkspaceId === activeWorkspaceId) {
      return;
    }

    let cancelled = false;

    const loadThreadsForWorkspace = async () => {
      const existingThreads = await intelligentChatService.loadExistingThreads(activeWorkspaceId);
      if (cancelled) {
        return;
      }

      if (existingThreads && existingThreads.length > 0) {
        const threadsWithMessages = await Promise.all(
          existingThreads.map(async (t: any) => {
            try {
              const messages = await intelligentChatService.loadThreadMessages(t.id);

              return {
                id: t.id,
                title: t.title || 'Conversa sem título',
                messages: messages.map((msg: any) => mapWorkspaceHistoryMessage(msg)),
                createdAt: new Date(t.createdAt),
                updatedAt: new Date(t.updatedAt),
                isActive: false,
                backendThreadId: t.id
              };
            } catch {
              return {
                id: t.id,
                title: t.title || 'Conversa sem título',
                messages: [],
                createdAt: new Date(t.createdAt),
                updatedAt: new Date(t.updatedAt),
                isActive: false,
                backendThreadId: t.id
              };
            }
          })
        );

        if (!cancelled) {
          setThreads(threadsWithMessages);
        }
      } else if (!cancelled) {
        setThreads([]);
      }

      if (!cancelled) {
        setThreadsHydratedForWorkspaceId(activeWorkspaceId);
      }
    };

    loadThreadsForWorkspace();

    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId, isBackendConnected, threadsHydratedForWorkspaceId, chatWorkspaces]);

  const forceSelectWorkspace = useCallback(async (nextWorkspaceId: string) => {
    if (nextWorkspaceId === activeWorkspaceId) {
      return;
    }
    try {
      await langchainIntegrationService.clearConversation();
    } catch {
      // ignore
    }
    setThreads([]);
    setActiveThreadId(null);
    setFloatingThreadId(null);
    setThreadsHydratedForWorkspaceId(null);
    setActiveWorkspaceId(nextWorkspaceId);
    sessionStorage.setItem(CHATINDOC_WORKSPACE_STORAGE_KEY, nextWorkspaceId);
  }, [activeWorkspaceId]);

  const selectWorkspace = useCallback(async (nextWorkspaceId: string) => {
    if (nextWorkspaceId === activeWorkspaceId) {
      return;
    }

    const hasOpenContext = threads.length > 0 || activeThreadId !== null;
    if (hasOpenContext) {
      const ok = window.confirm(
        'Trocar de agente encerra as conversas nesta tela e carrega as conversas do outro agente. Deseja continuar?'
      );
      if (!ok) {
        return;
      }
    }

    await forceSelectWorkspace(nextWorkspaceId);
  }, [activeWorkspaceId, threads.length, activeThreadId, forceSelectWorkspace]);

  const refreshChatWorkspaces = useCallback(async (options?: { forceNewSelection?: boolean }) => {
    const forceNewSelection = options?.forceNewSelection === true;
    const generation = ++workspaceRefreshGenerationRef.current;
    setIsLoadingWorkspace(true);
    setWorkspaceLoadError(null);
    setIsWorkspaceFeatureError(false);
    try {
      const summaries = await workspaceService.getChatInDocWorkspaces(undefined, {
        skipFeatureUnavailableToast: true,
      });
      if (generation !== workspaceRefreshGenerationRef.current)
      {
        return;
      }
      const workspaceList: ChatInDocWorkspace[] = summaries.map(s => ({
        id: s.id,
        name: s.name,
        slug: s.slug,
        assistantName: s.assistantName,
        description: s.description,
        avatar: s.avatar,
        isActive: s.isActive,
      }));
      const prevId = activeWorkspaceId;
      applyChatWorkspaceList(workspaceList, forceNewSelection);
      if (!workspaceList.length) {
        setThreads([]);
        setActiveThreadId(null);
        setThreadsHydratedForWorkspaceId(null);
        sessionStorage.removeItem(CHATINDOC_WORKSPACE_STORAGE_KEY);
      } else if (forceNewSelection || (prevId && !workspaceList.some(w => w.id === prevId))) {
        setThreads([]);
        setActiveThreadId(null);
        setThreadsHydratedForWorkspaceId(null);
      }
      queueMicrotask(() => {
        if (generation === workspaceRefreshGenerationRef.current)
        {
          setIsLoadingWorkspace(false);
        }
      });
    } catch (error) {
      if (generation !== workspaceRefreshGenerationRef.current)
      {
        return;
      }
      console.error('Erro ao recarregar workspaces ChatInDoc:', error);
      const loadError = resolveWorkspaceLoadError(error);
      setWorkspaceLoadError(loadError.message);
      setIsWorkspaceFeatureError(loadError.isFeatureError);
      setChatWorkspaces([]);
      setActiveWorkspaceId(null);
      setIsLoadingWorkspace(false);
    }
  }, [applyChatWorkspaceList, activeWorkspaceId]);

  const resetForTenantChange = useCallback(() => {
    workspaceRefreshGenerationRef.current += 1;
    try {
      langchainIntegrationService.clearConversation();
    } catch {
      // ignore
    }
    setIsLoadingWorkspace(true);
    setChatWorkspaces([]);
    setActiveWorkspaceId(null);
    setThreads([]);
    setActiveThreadId(null);
    setFloatingThreadId(null);
    setThreadsHydratedForWorkspaceId(null);
    sessionStorage.removeItem(CHATINDOC_WORKSPACE_STORAGE_KEY);
  }, []);

  const setActiveThread = async (id: string) => {
    // Só gerenciar memória se realmente trocou de thread
    if (activeThreadId !== id && activeThreadId !== null) {
      try {
        const { langchainIntegrationService } = await import('../services/langchainIntegrationService');
        await langchainIntegrationService.clearConversation();
        console.log('🧹 Memória LangChain limpa ao trocar thread:', activeThreadId, '→', id);

        // Carregar histórico da nova thread ativa na memória
        const currentThread = threads.find(t => t.id === id);
        if (currentThread && currentThread.messages.length > 0) {
          console.log('📚 Carregando histórico da thread na memória LangChain...');
          
          for (const message of currentThread.messages) {
            if (message.role === 'user') {
              await langchainIntegrationService.addUserMessageToMemory(message.content);
            } else if (message.role === 'assistant') {
              await langchainIntegrationService.addAssistantMessageToMemory(message.content);
            }
          }
          
          console.log('✅ Histórico carregado na memória:', currentThread.messages.length, 'mensagens');
        }
      } catch (error) {
        console.error('Erro ao gerenciar memória LangChain:', error);
      }
    } else if (activeThreadId === null) {
      console.log('🆕 Primeira thread sendo ativada - memória já foi limpa na criação');
    } else {
      console.log('🔄 Mesma thread - não precisa limpar memória');
    }

    setThreads(prev => prev.map(thread => ({
      ...thread,
      isActive: thread.id === id
    })));
    setActiveThreadId(id);
  };

  const addThread = async (
    title?: string,
    existingThreadId?: string,
    createInWorkspaceId?: string
  ): Promise<string> => {
    try {
      let threadId: string;
      let threadTitle: string;

      if (existingThreadId) {
        // Thread já foi criada no backend - apenas adicionar ao contexto local
        console.log('📋 Adicionando thread existente ao contexto local:', existingThreadId);
        threadId = existingThreadId;
        threadTitle = title || 'Nova conversa';
      } else {
        const workspaceForCreate = createInWorkspaceId ?? activeWorkspaceId ?? '';
        // Criar nova thread no backend
        console.log('🔄 Criando thread no backend...', { workspaceId: workspaceForCreate, title });

        const threadResponse = await intelligentChatService.startIntelligentThread({
          workspaceId: workspaceForCreate,
          title: title || 'Nova conversa',
        });

        console.log('✅ Thread criada no backend:', threadResponse);

        // Verificar se recebemos um threadId válido (GUID)
        if (!threadResponse.threadId || threadResponse.threadId.length < 10) {
          throw new Error('ThreadId inválido recebido do backend: ' + threadResponse.threadId);
        }

        threadId = threadResponse.threadId;
        threadTitle = threadResponse.title || title || 'Nova conversa';
      }

      // Limpar memória do LangChain APENAS para nova thread (não para thread existente)
      if (!existingThreadId) {
        try {
          const { langchainIntegrationService } = await import('../services/langchainIntegrationService');
          await langchainIntegrationService.clearConversation();
          console.log('🧹 Memória LangChain limpa para nova thread');
        } catch (error) {
          console.error('Erro ao limpar memória LangChain:', error);
        }
      } else {
        console.log('📋 Thread existente - não limpando memória');
      }

      // Criar thread local
      const newThread: ChatThread = {
        id: threadId,
        title: threadTitle,
        messages: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
        backendThreadId: threadId
      };

      setThreads(prev => [newThread, ...prev]);
      setActiveThreadId(newThread.id);
      
      console.log('✅ Thread local criada:', newThread.id);
      return newThread.id;
    } catch (error) {
      console.error('❌ Erro ao criar thread:', error);
      
      // NÃO criar fallback - lançar erro para o usuário saber que falhou
      throw new Error('Não foi possível criar a conversa. Verifique sua conexão.');
    }
  };

  const addMessage = async (threadId: string, message: ChatMessage) => {
    setThreads(prev => prev.map(thread => {
      if (thread.id === threadId) {
        const updatedThread = {
          ...thread,
          messages: [...thread.messages, message],
          updatedAt: new Date(),
          // Atualizar título se for a primeira mensagem do usuário
          title: thread.messages.length === 0 && message.role === 'user' 
            ? (message.content.length > 50 ? message.content.substring(0, 50) + '...' : message.content)
            : thread.title
        };

        // Se o título foi atualizado, sincronizar com o backend
        if (updatedThread.title !== thread.title && isBackendConnected) {
          
          // Chamar API para atualizar título no backend (assíncrono)
          intelligentChatService.updateThread({
            threadId: threadId,
            title: updatedThread.title
          }).then(() => {
           
          }).catch((error) => {
            console.error('Erro ao sincronizar título com backend:', error);
          });
        }

        return updatedThread;
      }
      return thread;
    }));
  };

  const updateMessage = (threadId: string, messageId: string, updates: Partial<ChatMessage>) => {
    setThreads(prev => prev.map(thread => {
      if (thread.id === threadId) {
        return {
          ...thread,
          messages: thread.messages.map(msg => 
            msg.id === messageId ? { ...msg, ...updates } : msg
          ),
          updatedAt: new Date()
        };
      }
      return thread;
    }));
  };

  const removeThread = async (id: string): Promise<void> => {
    try {
      // 1. Chamar API para remover do backend
      if (isBackendConnected && activeWorkspaceId) {
        await api.delete(`${intelligentChatService.baseUrl}/threads/${id}`);
      }
    } catch (error) {    
      // Continuar com a remoção local mesmo se a API falhar
    }

    // 2. Limpar memória do LangChain se for a thread ativa
    if (activeThreadId === id) {
      try {
        const { langchainIntegrationService } = await import('../services/langchainIntegrationService');
        await langchainIntegrationService.clearConversation();
        console.log('🧹 Memória LangChain limpa ao remover thread:', id);
      } catch (error) {
        console.error('Erro ao limpar memória LangChain:', error);
      }
    }

    // 3. Remover do estado local
    setThreads(prev => prev.filter(thread => thread.id !== id));
    
    // 4. Atualizar thread ativo se necessário
    if (activeThreadId === id) {
      const remainingThreads = threads.filter(thread => thread.id !== id);
      if (remainingThreads.length > 0) {
        setActiveThreadId(remainingThreads[0].id);
      } else {
        setActiveThreadId(null);
      }
    }
  };

  const showFloatingChat = (threadId: string) => {
    setFloatingThreadId(threadId);
  };

  const hideFloatingChat = () => {
    setFloatingThreadId(null);
  };

  // Funcionalidades de inteligência integradas com LangChain
  const analyzeIntent = async (query: string, threadId?: string) => {
    try {
      // Buscar histórico da conversa para contexto
      const currentThread = threads.find(t => t.id === threadId);
      const conversationHistory = currentThread?.messages || [];
      
      // Usar serviço integrado LangChain + Fallback
      const result = await langchainIntegrationService.analyzeIntentIntegrated(
        query, 
        conversationHistory, 
        [], // recentDocuments - pode ser implementado futuramente
        threadId
      );
      
      return {
        analysis: result.analysis,
        confidence: result.confidence,
        source: result.source,
        metadata: result.metadata
      };
    } catch (error) {
      // Fallback removido - usar apenas LangChain
      throw new Error('Análise de intenção não disponível - LangChain não está funcionando');
    }
  };

  // REMOVED: Orquestrador de Conversa Inteligente - using LangChain orchestrator
  const startIntelligentConversation = async (query: string, threadId?: string) => {
    // Use LangChain orchestrator instead
    throw new Error('Use LangChain orchestrator instead');
  };

  // REMOVED: Teste de Integração com Backend - using LangChain orchestrator
  const testBackendIntegrationDirectly = async (query: string, filters: any = {}) => {
    // Use LangChain orchestrator instead
    throw new Error('Use LangChain orchestrator instead');
  };

  // REMOVED: Teste Completo - service removed
  const runCompleteTest = async () => {
    return 'Test service removed - use LangChain orchestrator instead';
  };

  const searchIntelligently = async (query: string, threadId?: string) => {
    try {
      return await langchainIntegrationService.searchIntelligentlyIntegrated(query, threadId);
    } catch (error) {
      console.error('Erro na busca inteligente integrada:', error);
      // Fallback removido - usar apenas LangChain
      throw new Error('Busca inteligente não disponível - LangChain não está funcionando');
    }
  };

  const detectAmbiguity = async (query: string, threadId?: string) => {
    try {
      return await langchainIntegrationService.detectAmbiguityIntegrated(query, threadId);
    } catch (error) {
      console.error('Erro ao detectar ambiguidade integrada:', error);
      // Fallback removido - usar apenas LangChain
      throw new Error('Detecção de ambiguidade não disponível - LangChain não está funcionando');
    }
  };

  const value: ChatInDocContextType = {
    threads,
    activeThreadId,
    addThread,
    addMessage,
    removeThread,
    setActiveThread,
    // Funções que estavam faltando
    updateMessage: (threadId: string, messageId: string, updates: Partial<ChatMessage>) => {
      setThreads(prev => prev.map(thread => 
        thread.id === threadId 
          ? {
              ...thread,
              messages: thread.messages.map(msg => 
                msg.id === messageId ? { ...msg, ...updates } : msg
              )
            }
          : thread
      ));
    },
    floatingThreadId,
    showFloatingChat: (threadId: string) => setFloatingThreadId(threadId),
    hideFloatingChat: () => setFloatingThreadId(null),
    chatWorkspaces,
    workspace,
    activeWorkspaceId,
    selectWorkspace,
    forceSelectWorkspace,
    refreshChatWorkspaces,
    resetForTenantChange,
    isLoadingWorkspace,
    workspaceLoadError,
    isWorkspaceFeatureError,
    isBackendConnected,
    connectionStatus,
    analyzeIntent,
    searchIntelligently,
    detectAmbiguity,
    // NOVAS FUNCIONALIDADES PARA TESTE
    startIntelligentConversation,
    testBackendIntegrationDirectly,
    runCompleteTest
  };

  return (
    <ChatInDocContext.Provider value={value}>
      {children}
    </ChatInDocContext.Provider>
  );
};

export const useChatInDoc = () => {
  const context = useContext(ChatInDocContext);
  if (context === undefined) {
    throw new Error('useChatInDoc deve ser usado dentro de um ChatInDocProvider');
  }
  return context;
}; 